import { combineLatest, Observable, Subject } from 'rxjs'
import { map, switchMap, take } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { courseSelectors } from 'src/app/features/course/course.selectors'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { Subscriber } from 'src/app/features/subscription/subscriber/subscriber'
import { subscriberSelectors } from 'src/app/features/subscription/subscriber/subscriber.selectors'
import { subscriptionSelectors } from 'src/app/features/subscription/subscription.selectors'
import { Tag } from 'src/app/features/tag/tag'
import { tagSelectors } from 'src/app/features/tag/tag.selectors'
import { BulkAction } from 'src/app/shared/interfaces'

import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { DataTable } from '@pascalhonegger/ng-datatable'

import { settingSelectors } from '../../../../core/settings/settings.selectors'
import { ConfigurationService } from '../../../../shared/services/configuration.service'
import { getNumber } from '../../../../shared/until'
import { bookingItemSelectors } from '../../../booking/booking-item/booking-item.selectors'
import { BookingService } from '../../../booking/booking.service'
import { Participation } from '../../../booking/participation/participation'
import { participationSelectors } from '../../../booking/participation/participation.selectors'
import { Location } from '../../../location/location'
import { locationSelectors } from '../../../location/location.selectors'
import { Product } from '../../../product/product'
import { Sale } from '../../sale'
import { saleSelectors } from '../../sale.selectors'
import { SaleItem } from '../sale-item'
import { SaleItemService } from '../sale-item.service'
import { SimpleChanges, OnChanges } from '@angular/core'
import localNl from '@angular/common/locales/nl'
import moment from 'moment-mini'
import { bookingActions } from 'src/app/features/booking/booking.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'

@UntilDestroy()
@Component({
  selector: 'sale-item-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class SaleItemTableComponent
  implements OnInit, AfterViewChecked, OnChanges
{
  @Input() tableData: SaleItem[]
  @Input() bulkActions: BulkAction[] = []
  @Input() saleItemsAreAdjustable: boolean = true
  @Output() onBulkAction = new EventEmitter<any>()
  @Output() onClickRow = new EventEmitter<any>()
  @Output() onClickDelete = new EventEmitter<any>()

  @ViewChild('dataTable', { static: true }) dataTable: DataTable

  public productEntities$ = this.store.select(productSelectors.selectEntities)
  public tagEntities$: Observable<Dictionary<Tag>>
  public subscriptionEntities$ = this.store.select(
    subscriptionSelectors.selectEntities
  )
  public courseEntities$ = this.store.select(courseSelectors.selectEntities)
  public bookingEntities$ = this.store.select(bookingSelectors.selectEntities)

  public selectedLanguage$ = this.store.select(
    settingSelectors.selectById,
    'language'
  )

  public filterTerm: string = ''
  public allCheckedToggle: boolean = false
  public checkedIds: Map<number, boolean> = new Map()
  public selectedCustomer$: Observable<Member>
  public subscribers: Subscriber[]
  private subjectTableData$: Subject<any> = new Subject<any>()
  public collapsedRows: Map<number, boolean> = new Map()
  public bookingItemEntities$ = this.store.select(
    bookingItemSelectors.selectEntities
  )
  private productPrint: number[] = []
  public isLoading$: Observable<boolean>
  public productWithDate: any
  public participationsByDate: any
  public productWithDate$: Subject<any> = new Subject<any>()
  public currentPaint: any
  constructor(
    private store: Store<AppState>,
    public saleItemService: SaleItemService,
    public bookingService: BookingService,
    public configurationService: ConfigurationService,
    private detectChange: ChangeDetectorRef
  ) {}
  ngAfterViewChecked(): void {
    this.detectChange.detectChanges()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      JSON.stringify(changes?.tableData.currentValue) !==
      JSON.stringify(changes?.tableData.previousValue)
    ) {
      this.subjectTableData$.next(changes?.tableData.currentValue)
    }
  }
  ngOnInit() {
    this.subjectTableData$.pipe(take(1)).subscribe((saleItems) => {
      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              'participations.saleItemId': {
                [CondOperator.IN]: saleItems.map((s) => s.id)
              }
            },
            join: [
              { field: 'participations' },
              { field: 'product' },
              // { field: 'product.translations' },
              // { field: 'product.location' },
              { field: 'linkedProduct' }
              // { field: 'linkedProduct.translations' }
            ]
          })
        })
      )
    })

    this.tagEntities$ = this.store.select(tagSelectors.selectEntities)

    this.isLoading$ = combineLatest([
      this.store.select(bookingSelectors.selectIsLoading),
      this.store.select(saleSelectors.selectIsLoading)
    ]).pipe(
      map(([isLoadingBookings, isLoadingSales]) => {
        return isLoadingBookings || isLoadingSales
      })
    )

    this.store
      .select(subscriberSelectors.selectAll)
      .pipe(untilDestroyed(this))
      .subscribe((subscribers) => {
        this.subscribers = subscribers
      })

    this.selectedCustomer$ = this.store
      .select(saleSelectors.selectPartial)
      .pipe(
        take(1),
        switchMap((salePartial) =>
          this.store.select(
            memberSelectors.selectById,
            salePartial?.customerPersonId
          )
        )
      )
  }

  public onRowClicked(row: SaleItem) {
    this.onClickRow.emit(row)
  }

  public onCheckRow(targetRow: SaleItem) {
    this.checkedIds.set(
      targetRow.id,
      !Boolean(this.checkedIds.get(targetRow.id))
    )
  }

  public onToggleAllChecked() {
    this.allCheckedToggle = !this.allCheckedToggle

    this.dataTable.data.forEach((row) =>
      this.checkedIds.set(row.id, this.allCheckedToggle)
    )
  }

  public onClickBulkAction(bulkOption: BulkAction) {
    const rows: Sale[] = this.dataTable.data.filter((x) =>
      this.checkedIds.get(x.id)
    )
    if (rows.length > 0) {
      this.onBulkAction.emit({ action: bulkOption, items: rows })
    }
  }

  public getItemParticipants$(saleItem): Observable<Participation[]> {
    return this.store
      .select(participationSelectors.selectAll)
      .pipe(
        map((participations) =>
          participations.filter((p) => p.saleItemId == saleItem.id)
        )
      )
  }

  public getParticipants(item: SaleItem) {
    const guests = item?.params?.invitedGuests?.length
    const others = item?.params?.invitedOthers?.length

    return getNumber(guests) + getNumber(others)
  }

  public getParticipationsProductLocations(
    products: Product[]
  ): Observable<Location[]> {
    return combineLatest([
      this.store.select(locationSelectors.selectEntities),
      this.store.select(productSelectors.selectEntities)
    ]).pipe(
      map(([locationEntities, productEntities]) => {
        const uniqueLocationIds = [
          ...new Set(
            products
              .filter((p) => p.locationId)
              .map((p) => p.locationId)
              ?.filter(Boolean)
          )
        ]

        return uniqueLocationIds.map(
          (locationId) => locationEntities[locationId]
        )
      })
    )
  }

  public getTags$(saleItem: SaleItem): Observable<Tag[]> {
    return this.tagEntities$.pipe(
      map((tagEntities) => saleItem?.tags?.map((tagId) => tagEntities[tagId]))
    )
  }

  public getParticipationsProducts(saleItem: SaleItem): Observable<Product[]> {
    //combine selector to get all in 1 array to map it and filter by their id,
    return combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.store.select(bookingSelectors.selectEntities),
      this.store.select(participationSelectors.selectAll)
    ]).pipe(
      map(([productEntities, bookingEntities, participations]) => {
        const saleItemParticipations = participations.filter(
          (p) => p.saleItemId == saleItem.id
        )

        if (!saleItemParticipations?.length) {
          return []
        }

        const saleItemBookings = saleItemParticipations?.map(
          (participation) => bookingEntities[participation.bookingId]
        )

        if (!saleItemBookings?.length) {
          return []
        }

        const productIds = saleItemBookings
          .filter((b) => Boolean(b))
          .map((b) => b.productId)

        if (!productIds) {
          return []
        }

        const productDateIds = saleItemBookings
          .filter((b) => Boolean(b))
          .map((b) => {
            return {
              productId: b.productId,
              startDate: b.startDate,
              endDate: b.endDate
            }
          })

        const uniqueProductIds = [...new Set(productIds?.filter(Boolean))]

        const valueObject = Object.values(productDateIds).map(
          (x: any) => x.productId
        )
        const arrayOnject = [...new Set(valueObject?.filter(Boolean))]

        this.productWithDate = uniqueProductIds.map((productId) => {
          return {
            ...productEntities?.[productId],
            productId: saleItem.productId,
            startDate: productDateIds.find((x) => x.productId === productId)
              .startDate,
            endDate: productDateIds.find((x) => x.productId === productId)
              .endDate
          }
        })

        return uniqueProductIds.map((productId) => productEntities?.[productId])
      })
    )
  }
  public getDateOfProducts(id: number) {
    return this.productWithDate?.find((x) => x.productId == id)?.startDate
  }
  public getParticipationsProductsByDate(id: number) {
    this.participationsByDate = {}
    this.productWithDate.forEach((product) => {
      if (!this.participationsByDate.hasOwnProperty(product.startDate)) {
        this.participationsByDate[product.startDate] = {
          products: [],
          endDate: product.endDate
        }
      }

      this.participationsByDate[product.startDate].products.push({
        id: product.productId,
        description: product.description,
        startDate: product.startDate,
        endDate: product.endDate
      })
    })

    return Object.entries(this.participationsByDate)
  }
  public convertToArray(data: any) {
    return Object.entries(data)
  }
  public getDescription(data: any) {
    // if (this.currentPaint != data[1]) {
    //   this.currentPaint = data[1]
    //   return data[1].products.map((x) => x.description).join(',')
    // } else {
    //   return ''
    // }
    this.currentPaint = data[1]
    return data[1].products.map((x) => x.description).join(',')
  }

  public onCollapseRow(productId: number) {
    this.collapsedRows.set(productId, !this.collapsedRows.get(productId))
  }

  public onDeleteItemClicked(saleItem: SaleItem) {
    this.onClickDelete.emit(saleItem)
  }
  public setPrintProduct(id) {
    if (!this.productPrint.find((x) => x === id)) {
      this.productPrint.push(id)
    }
  }
  public productPrinted(item) {
    if (this.productPrint.find((x) => x === item.id)) {
      this.productPrint = this.productPrint.filter(
        (element) => element !== item.id
      )
      return true
    } else {
      return false
    }
  }
  public getCorrectFormatDate(date) {
    if (!date || date == 'null') {
      return
    }
    return moment(date).toDate()
  }
}
